import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/contexts/LanguageContext";
import { UNIT_BY_SLUG } from "@/data/units";

const CategoryPage = ({ slug: propSlug }) => {
  const params = useParams();
  const slug = propSlug || params.slug;
  const { t } = useLang();
  const unit = UNIT_BY_SLUG[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!unit) return <Navigate to="/" replace />;

  const data = t.units[unit.key];
  const cat = t.category[unit.key];

  return (
    <div
      data-testid={`category-page-${unit.key}`}
      className="min-h-screen bg-emka"
    >
      <Header />

      {/* Banner */}
      <section
        className="relative pt-[112px] lg:pt-[124px]"
        data-testid="category-banner"
      >
        <div className="relative h-[320px] sm:h-[400px] lg:h-[460px] overflow-hidden">
          <img
            src={unit.heroImage || unit.image}
            alt={`EMKA ${data.title}`}
            className="w-full h-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(11,16,32,0.45) 0%, rgba(11,16,32,0.96) 100%)",
            }}
          />
          <div className="absolute inset-0">
            <div className="max-w-[1200px] mx-auto h-full px-4 sm:px-6 lg:px-10 flex items-end pb-10 lg:pb-14">
              <div>
                <Link
                  to="/"
                  data-testid="back-home"
                  className="inline-flex items-center gap-2 text-white/80 hover:text-white text-xs font-semibold tracking-widest uppercase mb-4"
                >
                  <ArrowLeft size={14} />
                  {t.category.backHome}
                </Link>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <h1
                    className="font-display font-extrabold text-white uppercase leading-none"
                    style={{ fontSize: "clamp(2rem, 4.5vw, 3.6rem)" }}
                  >
                    EMKA
                  </h1>
                  <h1
                    className="font-display font-extrabold uppercase leading-none"
                    style={{
                      fontSize: "clamp(2rem, 4.5vw, 3.6rem)",
                      color: unit.accent,
                    }}
                  >
                    {data.title}
                  </h1>
                </div>
                {cat.tagline && (
                  <p
                    className="mt-3 text-white/85 font-medium"
                    style={{ fontSize: "clamp(1rem, 1.5vw, 1.15rem)" }}
                  >
                    {cat.tagline}
                  </p>
                )}
                {cat.partner && (
                  <div
                    data-testid="category-partner-badge"
                    className="mt-4 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] border"
                    style={{
                      borderColor: unit.accent,
                      color: unit.accent,
                      backgroundColor: "rgba(217,130,70,0.08)",
                    }}
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: unit.accent }}
                    />
                    {cat.partner}
                  </div>
                )}
                <span
                  className="mt-5 inline-block h-[3px] w-14"
                  style={{ backgroundColor: unit.accent }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section
        className="bg-emka py-14 lg:py-20"
        data-testid="category-overview"
      >
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <p
              className="text-[12px] font-bold tracking-[0.32em] uppercase mb-3"
              style={{ color: unit.accent }}
            >
              {t.category.overview}
            </p>
            <h2
              className="font-display font-extrabold text-white leading-tight mb-5"
              style={{ fontSize: "clamp(1.4rem, 2.4vw, 2rem)" }}
            >
              {t.catalogs[unit.key]}
            </h2>
            {cat.descriptionHtml ? (
              <div
                className="category-body text-white/85 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: cat.descriptionHtml }}
              />
            ) : (
              <p className="text-white/80 text-base leading-relaxed">
                {cat.intro}
              </p>
            )}
          </div>

          <div>
            <p
              className="text-[12px] font-bold tracking-[0.32em] uppercase mb-3"
              style={{ color: unit.accent }}
            >
              {t.category.capabilities}
            </p>
            <ul className="space-y-2.5">
              {data.items.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-white/90 text-sm"
                >
                  <Check
                    size={16}
                    style={{ color: unit.accent }}
                    className="mt-0.5 shrink-0"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Applications */}
      {cat.applications && cat.applications.length > 0 && (
        <section
          className="bg-emka-2 py-14 lg:py-20"
          data-testid="category-applications"
        >
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
            <p
              className="text-[12px] font-bold tracking-[0.32em] uppercase"
              style={{ color: unit.accent }}
            >
              {t.category.applicationsTitle}
            </p>
            <h2
              className="mt-2 font-display font-extrabold text-white leading-tight"
              style={{ fontSize: "clamp(1.4rem, 2.4vw, 2rem)" }}
            >
              {t.category.applicationsTitle}
            </h2>
            <span
              className="mt-4 inline-block h-[3px] w-14"
              style={{ backgroundColor: unit.accent }}
            />
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cat.applications.map((it, i) => (
                <div
                  key={i}
                  className="rounded-md border border-white/10 p-5 flex items-start gap-3"
                  style={{ backgroundColor: "#0c1330" }}
                >
                  <span
                    className="mt-1 h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: unit.accent }}
                  />
                  <span className="text-white/90 text-sm">{it}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      {cat.features && cat.features.length > 0 && (
        <section className="bg-emka py-14 lg:py-20" data-testid="category-features">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
            <p
              className="text-[12px] font-bold tracking-[0.32em] uppercase"
              style={{ color: unit.accent }}
            >
              {t.category.featuresTitle}
            </p>
            <h2
              className="mt-2 font-display font-extrabold text-white leading-tight"
              style={{ fontSize: "clamp(1.4rem, 2.4vw, 2rem)" }}
            >
              {t.category.featuresTitle}
            </h2>
            <span
              className="mt-4 inline-block h-[3px] w-14"
              style={{ backgroundColor: unit.accent }}
            />
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {cat.features.map((f, i) => (
                <div
                  key={i}
                  className="rounded-md p-5 border border-white/10 flex items-start gap-3"
                  style={{ backgroundColor: "#0c1330" }}
                >
                  <Sparkles
                    size={18}
                    style={{ color: unit.accent }}
                    className="mt-0.5 shrink-0"
                  />
                  <span className="text-white/90 text-sm leading-relaxed">
                    {f}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-emka-2 py-14" data-testid="category-cta">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
          <div
            className="rounded-md border border-white/10 p-8 sm:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
            style={{ backgroundColor: "#0c1330" }}
          >
            <div>
              <h3 className="font-display font-extrabold text-white text-2xl sm:text-3xl">
                {t.category.ctaTitle}
              </h3>
              <p className="mt-2 text-white/75 max-w-xl">
                {t.category.ctaText}
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="category-cta-button"
              className="inline-flex items-center gap-3 px-6 py-3 font-semibold uppercase tracking-[0.18em] text-sm text-neutral-900"
              style={{ backgroundColor: "var(--emka-gold)" }}
            >
              {t.category.ctaButton}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CategoryPage;
